/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { Check, Clock, FilterList, Plus, Search as SearchIcon, Sort } from 'iconoir-react'
import { compact, debounce, uniq } from 'lodash'
import moment from 'moment-timezone'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'urql'
import { Dropdown } from '../shared/styled/Dropdown'
import { Button } from 'app/components/ui/button'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { AddConversationModal } from './AddConversationModal'
import { ConversationList } from './ConversationList'
import { InboxLocationPicker } from './InboxLocationPicker'
import { useInboxLocations } from './useInboxLocations'
import { CountBadge } from 'app/components/inbox/CountBadge'
import { NotificationBadge } from 'app/components/inbox/NotificationBadge'
import type { ActivityContact } from 'app/types/entities'
import type { ContactJson, ContactType } from 'app/types/entities'
import type { LocationJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { INBOX_SORT_OPTIONS, isInboxSortOption, sortConversations } from 'app/lib/inboxSort'
import type { InboxSortOption, PinnedUnread } from 'app/lib/inboxSort'
import { INBOX_FILTER_OPTIONS, filterToRestore, isInboxFilterOption } from 'app/lib/inboxFilter'
import type { InboxFilterOption } from 'app/lib/inboxFilter'
import { useSettings } from 'app/hooks/useSettings'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useMediaQuery } from 'app/hooks/useMediaQuery'
import { useLocationOptOuts } from 'app/hooks/useLocationOptOuts'
import { useLocationTimezone } from 'app/hooks/useLocationTimezone'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { useContactStore } from 'app/stores/StoreContext'
import { useHandleSearchParams, useUser } from 'app/utils'
import { predictSmsThreadId } from 'app/lib/smsThreadId'
import { getConversationPhone } from 'app/lib/inboxConversations'
import { HELD_MESSAGES_QUERY, HELD_OR_RECENT_NOT_SENT } from 'app/graphql/scheduledMessages'
import { groupHeldRuns, heldOutcome, toHeldMessages } from 'app/lib/heldMessages'
import type { HeldMessage, HeldRun } from 'app/types/held-message'
import { useScheduledSend } from 'app/hooks/useScheduledSend'
import { sendAtLabel } from 'app/lib/scheduledSend'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { logWarn } from 'lib/logging'
import { NavItemType, SubnavType } from 'types/Sidebar'

/** A row in the Scheduled filter's list: either a grouped bulk run, or a single message. */
type ScheduledRow = { kind: `run`; run: HeldRun } | { kind: `single`; message: HeldMessage }

/** The naive wall-clock format `sendAtLabel` expects, matching `ScheduledTray`. */
const WALL_CLOCK_FORMAT = `YYYY-MM-DDTHH:mm`

/**
 * The Scheduled list's page size. The spec's own worked example is a
 * 400-recipient run, so this has room for one such run plus other queued
 * work; a run bigger than this truncates (see `scheduledTruncated` below)
 * rather than silently miscounting or letting Cancel all miss recipients
 * without saying so.
 */
const HELD_MESSAGES_LIMIT = 500

/**
 * The thread a held message will land in once it sends.
 *
 * A held message is always outbound (`sendSms` given a future `sendDate`), so
 * the patient's number is always `to`, unconditionally. A staff-scheduled send
 * carries `sentByUserId`, but an agent-scheduled one carries `isAgent` instead
 * (see the API's own poller tests) and has no `sentByUserId` at all, so
 * branching on `sentByUserId` to pick between `to` and `from` silently falls
 * back to `from` (the PRACTICE number) for exactly that case, mapping every
 * such row to the wrong thread or to none. Shared by the conversation-list
 * clock and by opening a row's conversation, so both resolve the same thread
 * the same way.
 */
function heldMessageThreadId(message: HeldMessage, location: LocationJson | undefined): string | undefined {
  // The row's own threadId when the API gave it one, which it does at creation.
  // The prediction behind it needs a loaded location whose primaryTrackingNumber
  // is the number that actually sent, and when either is untrue it returns an id
  // no conversation has, so the clock silently never renders.
  return message.threadId ?? predictSmsThreadId(location?.primaryTrackingNumber, message.to)
}

export type ConversationType = MessageJson & { total: number; unread: number; hasAI?: boolean }

export type { InboxSortOption }

// Matches the contact-resolution debounce in `useInboxSearch`, so the URL write
// and the lookup it triggers settle on the same pause.
const SEARCH_DEBOUNCE_MS = 250

const INBOX_SORT_SETTING_KEY = `inbox-sort`
const INBOX_FILTER_SETTING_KEY = `inbox-filter`
const INBOX_DEFAULT_SORT: InboxSortOption = `unread`

const SORT_LABELS: Record<InboxSortOption, string> = {
  newest: `Newest`,
  unread: `Unread`,
  needs_attention: `Needs attention`,
}

export type { InboxFilterOption }

const FILTER_LABELS: Record<InboxFilterOption, string> = {
  active: `Active`,
  unread: `Unread`,
  replied: `Replied`,
  ai_agent: `AI Agent`,
  scheduled: `Scheduled`,
  opted_out: `Opted out`,
  archived: `Archived`,
}

type InboxSubnavsProps = {
  conversations: ConversationType[]
  selectedThreadId: string
  selectedContactId?: string
  locations?: Record<string, number>
  /**
   * True until the first sync for the current location lands. Drives the
   * conversation-list skeleton so the list shows a loading transition on
   * initial load and after switching locations instead of flashing empty.
   */
  isLoadingConversations?: boolean
  /**
   * Thread IDs whose AI Agent has been manually toggled off. Visible
   * conversations with these IDs render the muted "AI Disabled" pill instead
   * of the active blue pill.
   */
  disabledThreadIds?: Set<string>
  threadNameOverrides?: Record<string, string>
  addConversation: (contact: ContactJson, threadId?: string) => void
  resetInboxState: () => void
  selectConversation: (threadId: string, contact?: ContactJson, contactId?: string) => void
  archiveConversation: (message: MessageJson, archived: boolean) => void
  markConversationUnread: (message: MessageJson) => void
  onConversationsChange: (updater: (prev: ConversationType[]) => ConversationType[]) => void
}

export type InboxSubnavsComponentProps = {
  conversations: ConversationType[]
  sortedConversations: ConversationType[]
  selectedThreadId: string
  selectedContactId?: string
  names: Record<string, string>
  threadNameOverrides?: Record<string, string>
  isCompact?: boolean
  relatedContacts?: Record<string, ActivityContact[]>
  isLoadingConversations?: boolean
  seenConversations: Record<string, string>
  location?: LocationJson
  locations?: Record<string, number>
  disabledThreadIds?: Set<string>
  /** Phone key to opt-out `createdDate` for the selected location. Drives the row badge. */
  optOuts: ReadonlyMap<string, string>
  selectedValue: InboxFilterOption
  sortBy: InboxSortOption
  query: string | undefined | null
  /** False when the workspace has exactly one Inbox-enabled location — nothing to switch to. */
  showLocationPicker: boolean
  isLocationSelected: boolean
  isArchivedFilter: boolean
  /** The scheduled-list props default inside the component (zero count, no
   *  rows) so a call site exercising only the `active` filter path, like the
   *  Storybook harness, doesn't have to know about the Scheduled filter at all. */
  isScheduledFilter?: boolean
  /** Whether the `scheduled-sms` toggle is on. Gates the Scheduled option out of
   *  the filter menu entirely; the toggle default in the Storybook harness. */
  scheduledSmsEnabled?: boolean
  scheduledCount?: number
  scheduledTimezone?: string | undefined
  scheduledRows?: ScheduledRow[]
  /** How many held messages are waiting on each thread, keyed by thread id, for
   *  the conversation list's clock hint. */
  scheduledCountsByThreadId?: Record<string, number>
  /** True when the API has more pending rows for this location than the page loaded. */
  scheduledTruncated?: boolean
  onAddConversation: (contact: ContactJson) => Promise<void>
  onSelectConversation: (conversation: ConversationType) => Promise<void>
  onSearchConversation: (query: string | undefined) => void
  onLocationChange: (location: LocationJson) => void
  onSortChange: (sort: InboxSortOption) => void
  onFilterChange: (filter: InboxFilterOption) => void
  onArchive: (message: MessageJson) => void
  onMarkUnread: (message: MessageJson) => void
  onCancelRun?: (ids: string[]) => void
  onOpenHeldMessage?: (message: HeldMessage) => void
}

export function useInboxSubnavs({
  conversations,
  selectedThreadId,
  selectedContactId,
  locations,
  isLoadingConversations,
  disabledThreadIds,
  threadNameOverrides,
  addConversation,
  resetInboxState,
  selectConversation,
  archiveConversation,
  markConversationUnread,
  onConversationsChange,
}: InboxSubnavsProps): InboxSubnavsComponentProps {
  const user = useUser()
  const scheduledSmsEnabled = useFeatureToggle(`scheduled-sms`)
  const contactStore = useContactStore()
  const handleSearchParams = useHandleSearchParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [relatedContacts, setRelatedContacts] = useState<Record<string, ActivityContact[]> | undefined>()
  const [names, setNames] = useState<Record<string, string>>({})
  // Compact (80px icon rail) applies ONLY to the tablet-width sidebar subnav
  // (768–1024px). Below md the list renders full-width in `main` as the mobile
  // master-detail surface, so it must not collapse to the rail there.
  const isTabletUp = useMediaQuery(`(min-width: 768px)`)
  const isCompact = isTabletUp && !useMediaQuery(`(min-width: 1024px)`)
  const { locations: accessibleLocations, loading: locationsLoading } = useInboxLocations()
  const [location, setLocation] = useState<LocationJson | undefined>()
  // Second call site on purpose: the subnav renders outside the inbox page and
  // cannot reach the controller's map. urql dedupes the request by operation
  // key, so both share one fetch; the badge can lag suppression by one poll on
  // first paint.
  const optOuts = useLocationOptOuts(location?.id)
  const locationId = searchParams.get(`locationId`) ?? undefined
  // Only the Scheduled rows read this, so with `scheduled-sms` off there is
  // nothing to label and the hook pauses on the absent id rather than costing
  // every inbox user a request.
  const scheduledTimezone = useLocationTimezone(scheduledSmsEnabled ? locationId : undefined)

  const [query, setQuery] = useState<string | undefined | null>(searchParams.get(`query`))
  const storageKey = useMemo(() => {
    if (user?.id) return `inbox-subnav-seen-${user.id}`
    return `inbox-subnav-seen`
  }, [user?.id])
  const [seenConversations, setSeenConversations] = useState<Record<string, string>>({})
  const [hasHydratedSeen, setHasHydratedSeen] = useState<boolean>(false)
  const storageKeyRef = useRef<string | undefined>(undefined)
  const [selectedValue, setSelectedValue] = useState<InboxFilterOption>(() => {
    const filter = searchParams.get(`filter`)
    return isInboxFilterOption(filter) ? filter : `active`
  })
  // Captured at mount because the URL loses the parameter later on (switching
  // location clears it), and that must not be read as the stored filter
  // becoming relevant again.
  const urlSpecifiedFilter = useRef(searchParams.has(`filter`))
  // The filter joins the sort below on the server-backed setting rather than
  // localStorage, so two controls sitting in the same header do not remember
  // over different spans.
  const [storedFilter, setStoredFilter] = useSettings<InboxFilterOption>(INBOX_FILTER_SETTING_KEY)
  const hasRestoredFilter = useRef(false)

  useEffect(() => {
    if (hasRestoredFilter.current) return
    const restored = filterToRestore(urlSpecifiedFilter.current, storedFilter)
    if (!restored) return
    hasRestoredFilter.current = true
    setSelectedValue(restored)
    resetInboxState()
    // The conversation query reads the filter from the URL rather than from
    // this state, so restoring the menu without the parameter would label a
    // list it is not showing. Replaced rather than pushed: this is where the
    // user asked to be, not a step back to somewhere they had been.
    const next = new URLSearchParams(window.location.search)
    next.set(`filter`, restored)
    setSearchParams(next, { replace: true })
  }, [storedFilter, resetInboxState, setSearchParams])
  // Unread-first is the default, but the choice is the user's and follows them
  // across devices via the server-backed user setting. The setting resolves after
  // first paint, so local state leads and hydrates from it once it lands.
  const [storedSort, setStoredSort] = useSettings<InboxSortOption>(INBOX_SORT_SETTING_KEY)
  const [sortByValue, setSortByValue] = useState<InboxSortOption>(INBOX_DEFAULT_SORT)

  useEffect(() => {
    // Anything unrecognized (a sort we have since dropped, a hand-edited value)
    // leaves the default in place rather than sorting by nothing.
    if (isInboxSortOption(storedSort)) setSortByValue(storedSort)
  }, [storedSort])

  const handleSortChange = useCallback(
    (sort: InboxSortOption) => {
      setSortByValue(sort)
      // useSettings debounces the write and updates its own state optimistically,
      // so the menu reflects the choice immediately either way.
      bg(setStoredSort(INBOX_SORT_SETTING_KEY, sort))
    },
    [setStoredSort],
  )

  const selectedConversation = useMemo(() => {
    if (selectedContactId) {
      return conversations.find((c) => c.contactId === selectedContactId)
    }
    return conversations.find((c) => c.threadId === selectedThreadId)
  }, [conversations, selectedThreadId, selectedContactId])

  const conversationPhonesKey = useMemo(
    () =>
      uniq(compact(conversations.map(getConversationPhone)))
        .sort()
        .join(`,`),
    [conversations],
  )
  const conversationContactIdsKey = useMemo(
    () =>
      uniq(compact(conversations.map((c) => c.contactId)))
        .sort()
        .join(`,`),
    [conversations],
  )

  const loadContact = async (contactId): Promise<ContactJson | undefined> => {
    if (!contactId) return
    const result = await contactStore.fetchOne(contactId)
    if (result.error) return
    // Return the interned instance (ADR-0002), not the raw `result.data.contact` copy, so the
    // conversation's `contact` flowing into PageContent is the one shared instance. Guard on the
    // id: a concurrent fetchOne for another contact could have moved `current`, so fall back to
    // the raw contact unless `current` is this id.
    const current = contactStore.current
    const loaded = (result.data as any)?.contact as ContactJson | undefined
    return current?.id === contactId ? current : loaded
  }

  const removeDrawerId = () => {
    handleSearchParams({ drawer: undefined })
  }

  const setTabId = (id: string | undefined | null) => {
    handleSearchParams({ tab: id ?? undefined })
  }

  const pendingSeenTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const pendingSeenThreadRef = useRef<string | undefined>(undefined)

  const markConversationAsSeen = useCallback(
    (conversation: ConversationType) => {
      if (!conversation.threadId || !conversation.id || !document.location.pathname.startsWith(`/inbox`)) return

      if (pendingSeenTimeoutRef.current) {
        clearTimeout(pendingSeenTimeoutRef.current)
        pendingSeenTimeoutRef.current = undefined
      }

      pendingSeenThreadRef.current = conversation.threadId
      pendingSeenTimeoutRef.current = setTimeout(() => {
        setSeenConversations((prev) => {
          const threadId = conversation.threadId!
          const previousVersion = prev[threadId]
          if (previousVersion === conversation.id) return prev
          return Object.assign({}, prev, { [threadId]: conversation.id! })
        })
      }, 3000)
    },
    [setSeenConversations],
  )

  const handleAddConversation = async (contact: ContactJson) => {
    let inConversation = conversations.find((c) => getConversationPhone(c) === contact.cellPhone)
    if (inConversation) {
      await handleSelectConversation(inConversation)
      return
    }
    const threadId = predictSmsThreadId(location?.primaryTrackingNumber, contact.cellPhone)
    const newConversation = {
      contactId: contact.id,
      to: contact.cellPhone,
      locationId: contact.locationId,
      sentByUserId: user?.id,
      total: 0,
      threadId,
      unread: 0,
    }
    onConversationsChange((prev) => [newConversation as ConversationType].concat(prev))
    if (contact.id && contact.cellPhone) {
      setRelatedContacts((prev) => {
        const updated = Object.assign({}, prev)
        updated[contact.cellPhone!] = [
          {
            id: contact.id!,
            name: contact.name,
            phone: contact.cellPhone,
          },
        ]
        return updated
      })
    }
    setNames((prev) => {
      if (contact.id && contact.name) {
        return Object.assign({}, prev, { [contact.id!]: contact.name })
      }
      return prev
    })
    markConversationAsSeen(newConversation as ConversationType)
    setTabId(threadId)
    removeDrawerId()
    addConversation(contact, threadId)
  }

  const handleSelectConversation = async (conversation: ConversationType) => {
    let contact = await loadContact(conversation.contactId)
    const activeConversation = conversations.find((c) => c.threadId === conversation.threadId)
    if (activeConversation) {
      markConversationAsSeen(activeConversation)
    }
    if (document.location.pathname.startsWith(`/inbox`)) {
      let contactForSelection: ContactJson | undefined = contact
      if (!contactForSelection) {
        const phone = getConversationPhone(conversation)
        contactForSelection = { cellPhone: phone } as ContactJson
      }
      selectConversation(conversation.threadId!, contactForSelection, conversation.contactId)
      setTabId(conversation.threadId)
      removeDrawerId()
    } else {
      window.location.href = `/inbox?tab=${conversation.threadId}&locationId=${conversation.locationId}`
    }
  }

  // Held in a ref so the debounced committer below can stay stable across renders
  // while still writing with the current searchParams.
  const applySearchRef = useRef<(next: string | undefined) => void>(() => {})
  applySearchRef.current = (next: string | undefined) => {
    handleSearchParams({
      query: next,
      ...(searchParams.has(`drawer`) ? { drawer: undefined } : {}),
      ...(searchParams.has(`tab`) ? { tab: undefined } : {}),
    })
  }

  // One URL write per pause instead of one per keystroke. `useHandleSearchParams`
  // pushes a history entry, so typing a name used to turn every character into
  // its own Back step.
  const commitSearch = useMemo(
    () => debounce((next: string | undefined) => applySearchRef.current(next), SEARCH_DEBOUNCE_MS),
    [],
  )
  useEffect(() => () => commitSearch.cancel(), [commitSearch])

  const handleSearchConversation = (query: string | undefined) => {
    setQuery(query)
    commitSearch(query)
  }

  const handleLocationChange = (location: LocationJson) => {
    const nextLocationId = location.id === `0` ? undefined : location.id
    // Re-selecting the already-open location must not wipe the list: the conversation refetch
    // (useInboxController) only re-runs when `locationId` changes, so clearing here with no param
    // change would leave an empty list and fire no fetch. Nothing to do when the id is unchanged.
    if (nextLocationId === (searchParams.get(`locationId`) ?? undefined)) return
    handleSearchParams({
      ...(searchParams.has(`drawer`) ? { drawer: undefined } : {}),
      ...(searchParams.has(`filter`) ? { filter: undefined } : {}),
      locationId: nextLocationId,
    })
    onConversationsChange(() => [])
  }

  const handleFilterChange = (filter: InboxFilterOption) => {
    setSelectedValue(filter)
    // From here on this user's own choice is the one to restore, whatever link
    // brought them here.
    urlSpecifiedFilter.current = false
    bg(setStoredFilter(INBOX_FILTER_SETTING_KEY, filter))
    setTabId(undefined)
    resetInboxState()
    handleSearchParams({
      filter: filter === `active` ? undefined : filter,
      ...(searchParams.has(`drawer`) ? { drawer: undefined } : {}),
    })
  }

  const handleArchive = useCallback(
    (message: MessageJson) => {
      archiveConversation(message, !message.archived)
      if (!message.archived && message.threadId === selectedThreadId) {
        setTabId(undefined)
        onConversationsChange((prev) => prev.filter((c) => c.threadId !== message.threadId))
      }
    },
    [archiveConversation, selectedThreadId, onConversationsChange],
  )

  // localStorage hydration
  useEffect(() => {
    if (typeof window === `undefined`) return
    if (storageKeyRef.current && storageKeyRef.current !== storageKey) {
      setSeenConversations({})
    }
    storageKeyRef.current = storageKey
    setHasHydratedSeen(false)
    try {
      const storedValue = window.localStorage.getItem(storageKey)
      if (storedValue) {
        const parsed = JSON.parse(storedValue)
        if (parsed && typeof parsed === `object`) {
          setSeenConversations((prev) => Object.assign({}, parsed as Record<string, string>, prev))
        }
      }
    } catch (error) {
      logWarn(`InboxSubnavs.loadSeenConversations`, error)
    } finally {
      setHasHydratedSeen(true)
    }
  }, [storageKey])

  // localStorage persistence
  useEffect(() => {
    if (!hasHydratedSeen || typeof window === `undefined`) return
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(seenConversations))
    } catch (error) {
      logWarn(`InboxSubnavs.persistSeenConversations`, error)
    }
  }, [hasHydratedSeen, seenConversations, storageKey])

  // Fetch related contacts
  useEffect(() => {
    if (!conversationPhonesKey || !searchParams.has(`locationId`)) {
      setRelatedContacts(undefined)
      return
    }
    bg(async () => {
      const cellPhones = conversationPhonesKey.split(`,`).join(` OR `)
      const locationId = searchParams.get(`locationId`)!
      const result = await contactStore.fetchList({
        search: `cellPhone:(${encodeURIComponent(cellPhones)}) AND locationIds:${locationId}`,
      })
      const hits = (result.data as any)?.contacts?.hits as ContactType[] | undefined
      const total = (result.data as any)?.contacts?.total as number | undefined
      if (hits && (total ?? 0) > 0) {
        let related = hits.reduce(
          (acc, c) => {
            const entry: ActivityContact = {
              id: c.id!,
              name: c.name,
              phone: c.cellPhone,
            }
            if (acc[c.cellPhone!]) {
              acc[c.cellPhone!] = [entry].concat(acc[c.cellPhone!])
            } else {
              acc[c.cellPhone!] = [entry]
            }
            return acc
          },
          {} as Record<string, ActivityContact[]>,
        )
        setRelatedContacts(related)
      }
    })
  }, [conversationPhonesKey, searchParams])

  // Fetch location
  useEffect(() => {
    if (searchParams.has(`locationId`)) {
      bg(async () => {
        // primaryTrackingNumber is required: handleAddConversation predicts the
        // server thread id from it, and predictSmsThreadId returns undefined
        // without it — which left every new conversation with no thread id.
        const result = await gqlFetchOne<LocationJson>(
          'location',
          searchParams.get(`locationId`)!,
          'id name primaryTrackingNumber',
        )
        if (!result) {
          logWarn(`Found no location for locationId ${searchParams.get(`locationId`)}`)
          return
        }
        setLocation(result as unknown as LocationJson)
      })
    } else if (location) {
      onConversationsChange(() => [])
      setLocation(undefined)
    }
  }, [searchParams])

  // Fetch contact names
  useEffect(() => {
    if (!conversationContactIdsKey) return
    bg(async () => {
      const ids = conversationContactIdsKey.split(`,`)
      const result = await contactStore.fetchList({ search: `id:(${ids.map((id) => `"${id}"`).join(` OR `)})` })
      const hits = (result.data as any)?.contacts?.hits as ContactType[] | undefined
      const total = (result.data as any)?.contacts?.total as number | undefined
      if (hits && (total ?? 0) > 0) {
        setNames(() => {
          let names = hits.reduce((acc, c) => {
            if (!c.name) return acc
            return Object.assign({}, acc, { [c.id!]: c.name })
          }, {}) as Record<string, string>
          return names
        })
      }
    })
  }, [conversationContactIdsKey])

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (pendingSeenTimeoutRef.current) {
        clearTimeout(pendingSeenTimeoutRef.current)
        pendingSeenTimeoutRef.current = undefined
      }
      pendingSeenThreadRef.current = undefined
    }
  }, [])

  // Mark initial conversation as seen
  useEffect(() => {
    if (selectedConversation) {
      markConversationAsSeen(selectedConversation)
    }
  }, [selectedConversation?.id])

  // Under the unread sort, reading a thread yanks it out from under the reader:
  // the dwell timer marks it read, its unread count drops to zero, and the row
  // slides down the list mid-conversation. Freeze the unread count the sort sees
  // for as long as the thread stays selected, then release it on deselect so the
  // row animates to its real position (the FLIP effect above picks that up).
  const [pinnedUnread, setPinnedUnread] = useState<PinnedUnread>()

  useEffect(() => {
    const threadId = selectedConversation?.threadId
    if (!threadId) {
      setPinnedUnread(undefined)
      return
    }
    const liveUnread = selectedConversation?.unread ?? 0
    setPinnedUnread((previous) => {
      // Switching threads always re-captures.
      if (previous?.threadId !== threadId) return { threadId, unread: liveUnread }
      // Same thread: the pin only ever ratchets up. Capturing once on selection
      // would miss a sync that lands the real unread count a tick after the
      // threadId changed, and would miss a new message arriving while the user
      // sits on the thread. Both should hold the row, and neither should be
      // undone when the dwell timer drops the live count back to zero.
      if (liveUnread <= previous.unread) return previous
      return { threadId, unread: liveUnread }
    })
  }, [selectedConversation?.threadId, selectedConversation?.unread])

  const sortedConversations = useMemo(
    () => sortConversations(conversations, sortByValue, pinnedUnread),
    [conversations, sortByValue, pinnedUnread],
  )

  // A stored setting or a shared link can carry `scheduled` while the toggle
  // is off (the menu only hides it going forward, it does not clear what is
  // already stored or in the URL). Falling back to `active` here keeps the
  // user off an empty "Nothing scheduled" panel with no menu item to explain
  // it, without touching the stored preference.
  const effectiveSelectedValue: InboxFilterOption =
    selectedValue === `scheduled` && !scheduledSmsEnabled ? `active` : selectedValue
  const scheduledFilterActive = effectiveSelectedValue === `scheduled`

  // A held message is a `Message` with a future `sendDate` (TRK-5656); there is
  // no ScheduledMessage entity, and the API declined an exclude-pending
  // argument, so the statuses wanted here are named explicitly. Runs for the
  // active location whenever the inbox is open, not only on the Scheduled
  // filter, so the filter's count is discoverable without selecting it first.
  const [{ data: heldData }, refetchHeld] = useQuery({
    query: HELD_MESSAGES_QUERY,
    variables: {
      search: `locationId:${locationId} AND ${HELD_OR_RECENT_NOT_SENT}`,
      pagination: { limit: HELD_MESSAGES_LIMIT },
      sort: { field: `sendDate`, direction: `ASC` },
    },
    // Gated on the toggle as well as the location: with `scheduled-sms` off,
    // nothing downstream can render a held row, so firing this would cost every
    // inbox user a 500 row query for a feature they cannot see.
    pause: !locationId || !scheduledSmsEnabled,
    requestPolicy: `cache-and-network`,
  })
  const heldMessages = useMemo(() => toHeldMessages(heldData?.messages?.hits), [heldData])
  // Whether the API has more pending rows for this location than the page
  // above loaded, so the view can say so instead of quietly showing a partial
  // list as if it were the whole thing.
  const scheduledTruncated = (heldData?.messages?.total ?? 0) > heldMessages.length
  const heldRuns = useMemo(() => groupHeldRuns(heldMessages), [heldMessages])
  const heldById = useMemo(() => new Map(heldMessages.map((message) => [message.id, message])), [heldMessages])

  // A run of one recipient reads as an ordinary queued row rather than "1
  // recipients"; a not-sent row (cancelled/failed) always stands on its own,
  // because it needs its own decision. The count badge (below) counts THIS
  // list, so a 400-recipient run reads as the one queued thing it is.
  const scheduledRows = useMemo(() => {
    const rows: ScheduledRow[] = []
    for (const run of heldRuns) {
      if (run.ids.length > 1) {
        rows.push({ kind: `run`, run })
        continue
      }
      const message = heldById.get(run.ids[0]!)
      if (message) rows.push({ kind: `single`, message })
    }
    for (const message of heldMessages) {
      if (heldOutcome(message).kind === `not_sent`) rows.push({ kind: `single`, message })
    }
    return rows
  }, [heldRuns, heldById, heldMessages])

  const orderedScheduledRows = useMemo(() => {
    const sendDateOf = (row: ScheduledRow) => (row.kind === `run` ? row.run.sendDate : (row.message.sendDate ?? ``))
    const decidesOf = (row: ScheduledRow) => row.kind === `single` && heldOutcome(row.message).kind === `not_sent`
    return [...scheduledRows].sort((a, b) => {
      const aDecides = decidesOf(a)
      const bDecides = decidesOf(b)
      if (aDecides !== bDecides) return aDecides ? -1 : 1
      return sendDateOf(a).localeCompare(sendDateOf(b))
    })
  }, [scheduledRows])

  // Maps each held message to the thread it will land in once it sends, by the
  // same phone-pair prediction `handleAddConversation` uses for a brand-new
  // conversation, rather than a second query. Reads the message's OWN `to`/
  // `from` rather than matching by contactId against `conversations`: a
  // contact can legitimately own more than one conversation row at a location
  // (TRK-5414 only merges threads sharing one normalized phone pair), so a
  // first-match-by-contact lookup could mark the wrong row for a contact with
  // two numbers.
  //
  // Counted, not just flagged, so the row can say how many are queued instead of
  // always claiming one. Only `pending` rows count: the search above also
  // returns recently cancelled and failed rows for the Scheduled list, and none
  // of those is waiting to go out, so a clock on their thread would be a lie.
  const scheduledCountsByThreadId = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const message of heldMessages) {
      if (message.status !== `pending`) continue
      const threadId = heldMessageThreadId(message, location)
      if (!threadId) continue
      counts[threadId] = (counts[threadId] ?? 0) + 1
    }
    return counts
  }, [heldMessages, location?.primaryTrackingNumber])

  const scheduledSend = useScheduledSend({ contactIds: [], timezone: scheduledTimezone })

  const handleCancelRun = useCallback(
    async (ids: string[]) => {
      const result = await scheduledSend.cancelMany(ids)
      // Always states the actual number cancelled, never "Cancelled" alone: if
      // this run was truncated to the page above, "Cancel all" only ever acted
      // on what was loaded, and the count must not imply it reached the whole
      // run. A partial failure is a real outcome the user needs told about,
      // not just something the console hears.
      if (result.failed > 0) {
        notifyError(
          `Cancelled ${result.cancelled} of ${ids.length} queued messages. ${result.failed} could not be cancelled.`,
        )
      } else {
        notifySuccess(`Cancelled ${result.cancelled} queued message${result.cancelled === 1 ? `` : `s`}.`)
      }
      refetchHeld({ requestPolicy: `network-only` })
    },
    [scheduledSend, refetchHeld],
  )

  const handleOpenHeldMessage = useCallback(
    async (message: HeldMessage) => {
      // Same resolution the clock uses: the held message's OWN predicted
      // thread, never a first-match on contactId (a contact can own more than
      // one conversation row; see `heldMessageThreadId`'s doc comment).
      const threadId = heldMessageThreadId(message, location)
      const match = threadId ? conversations.find((c) => c.threadId === threadId) : undefined
      if (match) {
        await handleSelectConversation(match)
        return
      }
      if (!message.contactId) return
      const contact = await loadContact(message.contactId)
      if (!contact?.cellPhone) return
      await handleSelectConversation({
        contactId: contact.id,
        to: contact.cellPhone,
        locationId: contact.locationId,
        threadId: threadId ?? predictSmsThreadId(location?.primaryTrackingNumber, contact.cellPhone),
      } as ConversationType)
    },
    [conversations, location?.primaryTrackingNumber],
  )

  return {
    conversations,
    selectedThreadId,
    selectedContactId,
    names,
    threadNameOverrides,
    isCompact,
    relatedContacts,
    isLoadingConversations,
    seenConversations,
    location,
    locations,
    disabledThreadIds,
    optOuts,
    selectedValue: effectiveSelectedValue,
    scheduledSmsEnabled,
    sortBy: sortByValue,
    query,
    sortedConversations,
    // A workspace with a single Inbox-enabled location has nothing to pick
    // between, so the picker is dead weight (useInboxPage already auto-selects
    // that location). While the list is still loading we withhold it rather
    // than showing a placeholder we may immediately remove.
    showLocationPicker: !locationsLoading && accessibleLocations.length !== 1,
    isLocationSelected: searchParams.has(`locationId`),
    isArchivedFilter: searchParams.has(`filter`) && searchParams.get(`filter`) === `archived`,
    isScheduledFilter: scheduledFilterActive,
    // One entry per run (a 400-recipient bulk send is one queued thing) plus
    // one per not-sent row, matching what `orderedScheduledRows` renders.
    scheduledCount: heldRuns.length + heldMessages.filter((message) => heldOutcome(message).kind === `not_sent`).length,
    scheduledTimezone,
    scheduledRows: orderedScheduledRows,
    scheduledCountsByThreadId,
    scheduledTruncated,
    onAddConversation: handleAddConversation,
    onSelectConversation: handleSelectConversation,
    onSearchConversation: handleSearchConversation,
    onLocationChange: handleLocationChange,
    onSortChange: handleSortChange,
    onFilterChange: handleFilterChange,
    onArchive: handleArchive,
    onMarkUnread: markConversationUnread,
    onCancelRun: handleCancelRun,
    onOpenHeldMessage: handleOpenHeldMessage,
  }
}

const NO_SCHEDULED_ROWS: ScheduledRow[] = []
const NO_SCHEDULED_COUNTS: Record<string, number> = {}

export function InboxSubnavsComponent({
  conversations,
  sortedConversations,
  selectedThreadId,
  selectedContactId,
  names,
  threadNameOverrides,
  isCompact,
  relatedContacts,
  isLoadingConversations,
  seenConversations,
  location,
  locations,
  disabledThreadIds,
  optOuts,
  selectedValue,
  sortBy: currentSort,
  query,
  showLocationPicker,
  isLocationSelected,
  isArchivedFilter,
  isScheduledFilter = false,
  scheduledSmsEnabled = false,
  scheduledCount = 0,
  scheduledTimezone,
  scheduledRows = NO_SCHEDULED_ROWS,
  scheduledCountsByThreadId = NO_SCHEDULED_COUNTS,
  scheduledTruncated = false,
  onAddConversation,
  onSelectConversation,
  onSearchConversation,
  onLocationChange,
  onSortChange,
  onFilterChange,
  onArchive,
  onMarkUnread,
  onCancelRun,
  onOpenHeldMessage,
}: InboxSubnavsComponentProps): SubnavType[] {
  if (!isLocationSelected) return []

  // Naive wall clock `sendAtLabel` expects, resolved from the stored instant
  // against the practice zone, matching `ScheduledTray`'s conversion.
  //
  // Presence is not validity: moment-timezone resolves an unrecognized zone name
  // as UTC without complaint, so a bogus zone would print a time that is hours
  // off and look authoritative. Every write path already checks `moment.tz.zone`
  // for that reason; this is display only, but a wrong time is worse than none.
  const sendLabel = (sendDate: string | undefined): string => {
    if (!sendDate || !scheduledTimezone || !moment.tz.zone(scheduledTimezone)) return ``
    const sendAtLocal = moment.tz(sendDate, scheduledTimezone).format(WALL_CLOCK_FORMAT)
    return sendAtLabel(sendAtLocal, scheduledTimezone)
  }

  // `INBOX_FILTER_OPTIONS` is the canonical vocabulary; this only trims what the
  // menu renders. The `scheduled-sms` toggle is off by default, and its spec
  // requires every surface to behave exactly as it does today while it is off,
  // so the option stays out of the menu until the toggle is on.
  const visibleFilterOptions = INBOX_FILTER_OPTIONS.filter((option) => option !== `scheduled` || scheduledSmsEnabled)

  // Rendered next to the location picker when there is one, and folded into the
  // search row when there isn't, so a single-location workspace doesn't keep a
  // whole toolbar row alive for one button.
  const newConversationButton = (
    <TooltipWrapper
      placement="bottom"
      message={isArchivedFilter ? `Switch off the archived filter to start a new conversation` : `New conversation`}
    >
      <button
        type="button"
        aria-label="New conversation"
        disabled={!isLocationSelected || isArchivedFilter}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-primary-500 bg-surface text-primary-500 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-default disabled:text-minimal"
        onClick={() =>
          showModal(
            <div className="w-[800px]">
              <AddConversationModal
                conversations={conversations.reduce(
                  (acc, c) => Object.assign({}, acc, { [c.contactId!]: c.total }),
                  {},
                )}
                onContactSelect={onAddConversation}
              />
            </div>,
            {
              title: `Start a new conversation`,
              isFullScreen: false,
              removePadding: true,
              useRadix: true,
            },
          )
        }
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </TooltipWrapper>
  )

  return [
    {
      name: ``,
      children: compact([
        {
          filter: (
            <div
              className={classNames(
                `-order-1 flex flex-col divide-y divide-subtle bg-surface`,
                isCompact ? `w-[80px]` : `w-full md:w-[380px]`,
              )}
            >
              <div
                className={classNames(
                  `flex h-app-header shrink-0 items-center`,
                  isCompact ? `justify-center px-2` : `px-4`,
                )}
              >
                <span className="text-heading-sm font-semibold text-strong">Inbox</span>
              </div>
              <div className={classNames(`flex flex-col gap-y-2 pb-3 pt-3`, isCompact ? `items-center px-2` : `px-4`)}>
                {(showLocationPicker || isCompact) && (
                  <div className={classNames(`flex items-center gap-x-2`, isCompact ? `flex-col gap-y-2` : ``)}>
                    {showLocationPicker && (
                      <div className={classNames(`relative`, isCompact ? `` : `flex-1`)}>
                        <InboxLocationPicker
                          location={location}
                          unreadByLocation={locations}
                          compact={isCompact}
                          onChange={onLocationChange}
                        />
                        {/* The expanded trigger carries a counted chip, so the
                            dot is only worth drawing on the 32px icon rail,
                            where a chip would overflow the button. */}
                        {isCompact && (
                          <NotificationBadge
                            showNotification={Object.entries(locations ?? {}).some(
                              ([id, count]) => id !== location?.id && count > 0,
                            )}
                          />
                        )}
                      </div>
                    )}
                    {newConversationButton}
                  </div>
                )}

                {!isCompact && (
                  <div className="flex items-center gap-x-2">
                    <div
                      className={classNames(
                        `flex h-8 flex-1 items-center gap-x-2 rounded-md border border-default px-2`,
                        !isLocationSelected ? `cursor-not-allowed bg-surface-subtle` : `bg-surface`,
                      )}
                    >
                      <SearchIcon className="h-4 w-4 shrink-0 text-subtle" strokeWidth={2} />
                      <input
                        type="text"
                        defaultValue={query ?? ``}
                        disabled={!isLocationSelected}
                        placeholder="Search name or number"
                        className="min-w-0 flex-1 border-none bg-transparent p-0 text-body-xs text-strong outline-none placeholder:text-minimal focus:ring-0 disabled:cursor-not-allowed"
                        onChange={(evt) => onSearchConversation(evt.target.value.trim())}
                      />
                    </div>
                    <Dropdown
                      placement="bottom-end"
                      closeOnClick
                      className="w-48 shadow-elevation-2"
                      button={
                        <TooltipWrapper placement="bottom" message={`Sort: ${SORT_LABELS[currentSort]}`}>
                          <button
                            type="button"
                            aria-label={`Sort: ${SORT_LABELS[currentSort]}`}
                            disabled={!isLocationSelected}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-default bg-surface text-default transition-colors hover:bg-surface-subtle disabled:cursor-not-allowed disabled:text-minimal"
                          >
                            <Sort className="h-3.5 w-3.5" strokeWidth={2} />
                          </button>
                        </TooltipWrapper>
                      }
                    >
                      <div className="py-1">
                        {INBOX_SORT_OPTIONS.map((option) => (
                          <Button
                            key={option}
                            type="button"
                            variant="ghost"
                            size="row"
                            className="justify-between"
                            onClick={() => onSortChange(option)}
                          >
                            <span
                              className={classNames(
                                `text-label-xs`,
                                currentSort === option ? `font-semibold text-strong` : `text-default`,
                              )}
                            >
                              {SORT_LABELS[option]}
                            </span>
                            {currentSort === option && <Check className="h-4 w-4 text-primary-500" strokeWidth={2} />}
                          </Button>
                        ))}
                      </div>
                    </Dropdown>
                    <Dropdown
                      placement="bottom-end"
                      closeOnClick
                      className="w-48 shadow-elevation-2"
                      button={
                        <TooltipWrapper placement="bottom" message={`Filter: ${FILTER_LABELS[selectedValue]}`}>
                          <button
                            type="button"
                            aria-label={`Filter: ${FILTER_LABELS[selectedValue]}`}
                            disabled={!isLocationSelected}
                            className={classNames(
                              `relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-default bg-surface text-default transition-colors hover:bg-surface-subtle disabled:cursor-not-allowed disabled:text-minimal`,
                            )}
                          >
                            <FilterList className="h-3.5 w-3.5" strokeWidth={2} />
                            {selectedValue !== `active` && (
                              <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-blue-600" />
                            )}
                          </button>
                        </TooltipWrapper>
                      }
                    >
                      <div className="py-1">
                        {visibleFilterOptions.map((option) => (
                          <Button
                            key={option}
                            type="button"
                            variant="ghost"
                            size="row"
                            className="justify-between"
                            onClick={() => onFilterChange(option)}
                          >
                            <span
                              className={classNames(
                                `flex items-center gap-1.5 text-label-xs`,
                                selectedValue === option ? `font-semibold text-strong` : `text-default`,
                              )}
                            >
                              {FILTER_LABELS[option]}
                              {option === `scheduled` && <CountBadge value={scheduledCount} />}
                            </span>
                            {selectedValue === option && <Check className="h-4 w-4 text-primary-500" strokeWidth={2} />}
                          </Button>
                        ))}
                      </div>
                    </Dropdown>
                    {!showLocationPicker && newConversationButton}
                  </div>
                )}
              </div>
            </div>
          ),
          // A held message is a `Message` with a future `sendDate`; there is no
          // campaign entity and no body editor here, only cancel. Changing a
          // queued message happens in the thread's own tray (`ScheduledTray`).
          customComponent: isScheduledFilter ? (
            <div
              className={classNames(
                `flex min-h-0 grow flex-col overflow-y-auto`,
                isCompact ? `max-w-[80px]` : `max-w-full md:max-w-[380px]`,
              )}
            >
              {scheduledTruncated && (
                <p className="border-b border-trn-100 bg-amber-50 px-4 py-2 text-body-xs text-amber-800">
                  Showing the first {HELD_MESSAGES_LIMIT}. There are more queued than this list can show.
                </p>
              )}
              {scheduledRows.length === 0 ? (
                <p className="px-4 py-6 text-center text-body-sm text-trn-500">Nothing scheduled</p>
              ) : (
                scheduledRows.map((row) => {
                  if (row.kind === `run`) {
                    return (
                      <div
                        key={row.run.key}
                        className="flex items-start justify-between gap-x-2 border-b border-trn-100 px-4 py-4"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-body-sm font-medium text-neutral-900">
                            {row.run.ids.length} recipients
                          </p>
                          <p className="mt-0.5 flex items-center gap-x-1 text-body-xs text-neutral-500">
                            <Clock className="h-3 w-3 shrink-0" strokeWidth={2} />
                            Sends {sendLabel(row.run.sendDate) || `at a later time`}
                          </p>
                        </div>
                        <Button type="button" variant="link" size="sm" onClick={() => onCancelRun?.(row.run.ids)}>
                          Cancel all
                        </Button>
                      </div>
                    )
                  }
                  const outcome = heldOutcome(row.message)
                  const notSent = outcome.kind === `not_sent`
                  return (
                    <button
                      key={row.message.id}
                      type="button"
                      className="flex w-full flex-col items-start gap-y-0.5 border-b border-trn-100 px-4 py-4 text-left transition-colors hover:bg-trn-50"
                      onClick={() => onOpenHeldMessage?.(row.message)}
                    >
                      <p className="line-clamp-2 w-full text-body-sm text-neutral-900">{row.message.text}</p>
                      {notSent ? (
                        <p className="text-body-xs text-amber-700">Not sent. {outcome.reason}</p>
                      ) : (
                        <p className="flex items-center gap-x-1 text-body-xs text-neutral-500">
                          <Clock className="h-3 w-3 shrink-0" strokeWidth={2} />
                          Sends {sendLabel(row.message.sendDate) || `at a later time`}
                        </p>
                      )}
                    </button>
                  )
                })
              )}
            </div>
          ) : (
            <ConversationList
              sortedConversations={sortedConversations}
              selectedThreadId={selectedThreadId}
              selectedContactId={selectedContactId}
              names={names}
              threadNameOverrides={threadNameOverrides}
              isCompact={isCompact}
              relatedContacts={relatedContacts}
              isLoadingConversations={isLoadingConversations}
              seenConversations={seenConversations}
              disabledThreadIds={disabledThreadIds}
              optOuts={optOuts}
              scheduledCountsByThreadId={scheduledCountsByThreadId}
              onSelectConversation={onSelectConversation}
              onArchive={onArchive}
              onMarkUnread={onMarkUnread}
            />
          ),
        },
      ]) as NavItemType[],
    },
  ] as SubnavType[]
}

// --- Container ---

export const InboxSubnavs = (props: InboxSubnavsProps): SubnavType[] => {
  const componentProps = useInboxSubnavs(props)
  return InboxSubnavsComponent(componentProps)
}
